// src/pages/ContentEditor/Index.js
import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../../auth/AuthContext";
import TextEditor from "./TextEditor";
import Editorpop from "../../components/Editorpop";
import FlowchartEditor from "./FlowChartEditor"; // Import the new FlowchartEditor
import { Helmet } from "react-helmet";
import { FaBold, FaItalic } from "react-icons/fa";
// import { FaRegSave } from "react-icons/fa"; // Currently unused
import { BiImage, BiCode } from "react-icons/bi";
import { VscSymbolMisc } from "react-icons/vsc";
import CanvasPreview from "./CanvasPreview";

const ContentEditor = () => {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [isFlowchartModalOpen, setIsFlowchartModalOpen] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [editorId, setEditorId] = useState(`draft-${Date.now()}`);
  const [saveStatus, setSaveStatus] = useState("");
  const [images, setImages] = useState({}); // {filename: base64data}
  const { user } = useAuth();
  const editorRef = useRef(null);
  const [contentHistory, setContentHistory] = useState([""]); // History stack for undo
  const [historyIndex, setHistoryIndex] = useState(0); // Current position in history
  const [fontSize, setFontSize] = useState("");
  // Load saved draft from localStorage on component mount
  useEffect(() => {
    try {
      const savedDraft = localStorage.getItem("editor-current-draft");
      if (savedDraft) {
        const draftData = JSON.parse(savedDraft);
        setTitle(draftData.title || "");
        setContent(draftData.content || "");
        setContentHistory([draftData.content || ""]); // Initialize history with loaded content
        setImages(draftData.images || {});
        setEditorId(draftData.id || `draft-${Date.now()}`); // Fallback for editorId
        setIsDirty(true);
        setSaveStatus("Draft loaded");

        setTimeout(() => setSaveStatus(""), 2000);
      } else {
        // If no saved draft, initialize history with empty content
        setContentHistory([""]);
      }
    } catch (error) {
      console.error("Error loading draft from localStorage:", error);
      // Initialize history even if an error occurs
      setContentHistory([""]);
    }
  }, []);

  // Save draft to localStorage whenever content, title, or images change
  useEffect(() => {
    if (title || content || Object.keys(images).length > 0) {
      setIsDirty(true);
      const saveTimeout = setTimeout(() => {
        try {
          const draftData = {
            id: editorId,
            title,
            content,
            images, // Save images to localStorage
            lastEdited: new Date().toISOString(),
          };
          localStorage.setItem("editor-current-draft", JSON.stringify(draftData));
          setSaveStatus("Draft saved");

          setTimeout(() => setSaveStatus(""), 2000);
        } catch (error) {
          console.error("Error saving draft to localStorage:", error);
          setSaveStatus("Error saving draft");
        }
      }, 1000);

      return () => clearTimeout(saveTimeout);
    }
  }, [title, content, images, editorId]); // Add images to dependency array

  // Update content history whenever content changes
  useEffect(() => {
    if (content !== contentHistory[historyIndex]) {
      const newHistory = [...contentHistory.slice(0, historyIndex + 1), content];
      setContentHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
    }
  }, [content, contentHistory, historyIndex]);

  // Handle Ctrl+Z for undo
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === "z") {
        e.preventDefault();
        if (historyIndex > 0) {
          const newIndex = historyIndex - 1;
          setHistoryIndex(newIndex);
          setContent(contentHistory[newIndex]);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [historyIndex, contentHistory]);

  // Handle window close/navigation events
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty) {
        const message =
          "You have unsaved changes. Your draft has been saved, but are you sure you want to leave?";
        e.returnValue = message;
        return message;
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isDirty]);

  const toggleMobilePreview = () => {
    setShowPreview(!showPreview);
  };

  const handleAuthRequiredAction = (action) => {
    if (!user) {
      setIsPopupOpen(true);
      return false;
    }
    return true;
  };

  const handleSave = () => {
    if (!handleAuthRequiredAction("save")) return;

    console.log("Saving content:", { id: editorId, title, content });

    alert("Content saved successfully!");

    localStorage.removeItem("editor-current-draft");

    const newDraftId = `draft-${Date.now()}`;
    setEditorId(newDraftId);

    setIsDirty(false);
    setImages({}); // Clear images after saving
    setContentHistory([""]); // Reset history
    setHistoryIndex(0);
  };

  const handlePublish = () => {
    if (!handleAuthRequiredAction("publish")) return;

    if (!title.trim()) {
      alert("Please add a title before publishing");
      return;
    }

    if (content.length < 20) {
      alert("Content is too short. Please add more content before publishing.");
      return;
    }

    console.log("Publishing content:", { id: editorId, title, content });

    alert("Content published successfully!");

    localStorage.removeItem("editor-current-draft");

    const newDraftId = `draft-${Date.now()}`;
    setEditorId(newDraftId);
    setTitle("");
    setContent("");
    setImages({}); // Clear images after publishing
    setContentHistory([""]); // Reset history
    setHistoryIndex(0);

    setIsDirty(false);
  };

  const handleInsertImage = () => {
    document.getElementById("image-upload").click();
  };

  const handleImageSelected = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const filename = file.name;
    const reader = new FileReader();
    reader.onload = () => {
      setImages((prev) => ({
        ...prev,
        [filename]: reader.result,
      }));
      setContent((prev) => prev + `\n[Image: ${filename}]\n`);
    };
    reader.readAsDataURL(file);
  };

  const handleOpenFlowchart = () => {
    setIsFlowchartModalOpen(true);
  };

  const handleInsertFlowchart = ({ imageData, filename }) => {
    setImages((prev) => ({
      ...prev,
      [filename]: imageData,
    }));
    setContent((prev) => prev + `\n[Image: ${filename}]\n`);
    setIsFlowchartModalOpen(false);
  };

  const handleFontSizeChange = (size) => {
    const textarea = editorRef.current?.editorRef?.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = content.substring(start, end);

    if (selectedText) {
      const newText = `[FontSize:${size}]${selectedText}[/FontSize]`;
      setContent(
        content.substring(0, start) + newText + content.substring(end)
      );
    } else {
      setContent((prev) => prev + `[FontSize:${size}]New text[/FontSize]`);
    }
  };

  const handleBold = () => {
    const textarea = editorRef.current?.editorRef?.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = content.substring(start, end);

    if (selectedText) {
      const newText = `**${selectedText}**`;
      setContent(
        content.substring(0, start) + newText + content.substring(end)
      );
    } else {
      setContent((prev) => prev + `**New bold text**`);
    }
  };

  const handleItalic = () => {
    const textarea = editorRef.current?.editorRef?.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = content.substring(start, end);

    if (selectedText) {
      const newText = `_${selectedText}_`;
      setContent(
        content.substring(0, start) + newText + content.substring(end)
      );
    } else {
      setContent((prev) => prev + `_New italic text_`);
    }
  };

  const handleCode = () => {
    const textarea = editorRef.current?.editorRef?.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = content.substring(start, end);

    if (selectedText) {
      const newText = `\`${selectedText}\``;
      setContent(
        content.substring(0, start) + newText + content.substring(end)
      );
    } else {
      setContent((prev) => prev + `\`New code\``);
    }
  };

  // Function to parse and render the title with formatting
  const renderTitle = (titleText) => {
    let formattedTitle = titleText || "Untitled";

    // Apply font size formatting
    formattedTitle = formattedTitle.replace(
      /\[FontSize:(\d+)\](.*?)\[\/FontSize\]/g,
      '<span style="font-size: $1px">$2</span>'
    );

    // Apply bold formatting
    formattedTitle = formattedTitle.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");

    // Apply italic formatting
    formattedTitle = formattedTitle.replace(/_(.*?)_/g, "<em>$1</em>");

    return formattedTitle;
  };



  return (
    <div className="w-full h-full overflow-hidden pb-16">
      <Helmet>
        <title>Content Editor | limkc.com</title>
        <meta name="description" content="Create and edit content for your blog or projects." />
      </Helmet>

      <Editorpop
        isOpen={isPopupOpen}
        onClose={() => setIsPopupOpen(false)}
        customTitle="Sign up to access editor"
        customMessage="Create an account or log in to save and publish your content."
      />

      <input
        type="file"
        id="image-upload"
        accept="image/*"
        className="hidden"
        onChange={handleImageSelected}
      />

<div className="px-6 py-3 pt-[calc(var(--navbar-height)+0.75rem)]">
        <h1 className="text-2xl font-bold text-gray-800 mb-6">Content Editor</h1>
        <input
          type="text"
          placeholder="Enter title..."
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="w-full font-bold px-4 py-3 text-xl text-white placeholder-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-cyan-400 border-0 mb-6 bg-gray-700/30 backdrop-blur-sm"
          style={{
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            backgroundColor: 'rgba(55, 65, 81, 0.3)'
          }}
        />

      </div>

      <div className="flex flex-col md:flex-row w-full h-full flex-grow">

      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">


          <div className="bg-gray-100 p-2 rounded-t-md flex flex-wrap items-center gap-2 border border-gray-300">
            <button
              onClick={handleInsertImage}
              className="p-2 rounded hover:bg-gray-200 transition"
              title="Insert Image"
            >
              <BiImage />
            </button>
            <button
              onClick={handleOpenFlowchart}
              className="p-2 rounded hover:bg-gray-200 transition"
              title="Create Flowchart"
            >
              <VscSymbolMisc />
            </button>
            <div className="border-l border-gray-300 h-6 mx-1"></div>

   
<select
  value={fontSize}
  onChange={(e) => {
    setFontSize(e.target.value);
    handleFontSizeChange(e.target.value);
  }}
  className="p-2 rounded-2xl bg-gray-700/30 backdrop-blur-sm text-white border-0 focus:outline-none focus:ring-2 focus:ring-cyan-400 hover:bg-gray-600/30 transition text-sm"
  style={{
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    backgroundColor: 'rgba(55, 65, 81, 0.3)'
  }}
  title="Font Size"
>
  <option value="" disabled>Font Size</option>
  <option value="12">12px</option>
  <option value="14">14px</option>
  <option value="16">16px</option>
  <option value="18">18px</option>
  <option value="20">20px</option>
  <option value="24">24px</option>
  <option value="30">30px</option>
</select>

            <button
              onClick={handleBold}
              className="p-2 rounded hover:bg-gray-200 transition"
              title="Bold"
            >
              <FaBold />
            </button>

            <button
              onClick={handleItalic}
              className="p-2 rounded hover:bg-gray-200 transition"
              title="Italic"
            >
              <FaItalic />
            </button>

            <button
              onClick={handleCode}
              className="p-2 rounded hover:bg-gray-200 transition"
              title="Code"
            >
              <BiCode />
            </button>
          </div>

          <div className="border border-t-0 border-gray-300 rounded-b-md overflow-auto" style={{ height: "calc(100vh - 220px)", maxWidth: "100%" }}>
            <TextEditor
              ref={editorRef}
              content={content}
              setContent={setContent}
              setImages={setImages}
              placeholder="Start writing or drag files here..."
            />
          </div>

          {/* Buttons placed directly below the editor box */}
 {/* Buttons placed directly below the editor box */}
{/* Buttons placed directly below the editor box */}
<div className="mt-2 flex justify-end space-x-2 items-center">
  <button
    onClick={handlePublish}
    className="px-2 py-1 bg-blue-600 hover:bg-green-700 text-xs text-white rounded-md"
  >
    Publish
  </button>
  <button
    onClick={handleSave}
    className={`px-2 py-1 bg-blue-600 hover:bg-gray-700 text-xs text-white rounded-md ${
      !isDirty ? "opacity-50 cursor-not-allowed" : ""
    }`}
    disabled={!isDirty}
  >
    Save
  </button>

  {/* ✅ Preview Toggle - Styled same as Publish/Save - Mobile only */}
  <button
    onClick={toggleMobilePreview}
    className="px-2 py-1 bg-gray-100 hover:bg-gray-200 text-xs text-gray-800 rounded-md transition md:hidden"
  >
    {showPreview ? "Hide Preview" : "Show Preview"}
  </button>
</div>

{/* ✅ Preview section toggles BELOW the button row */}
{showPreview && (
  <div className="mt-4 border border-gray-300 rounded-md p-4 bg-white overflow-auto h-full">
    <h2 className="text-2xl font-bold text-gray-700 mb-4" dangerouslySetInnerHTML={{ __html: renderTitle(title) }} />
    <CanvasPreview content={content} images={images} mode="blog" />
  </div>
)}

</div>  {/* Closing main flex container */}
</div>
{/* ✅ Save Status Floating */}
{saveStatus && (
  <div className="fixed bottom-4 right-4 z-30 text-xs text-gray-500 bg-white bg-opacity-80 px-2 py-1 rounded-md shadow">
    {saveStatus}
  </div>
)}

{/* ✅ Flowchart Modal */}
{isFlowchartModalOpen && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white p-4 rounded-lg shadow-lg w-full max-w-4xl max-h-[90vh] overflow-auto">
      <div className="flex justify-between items-center mb-4 border-b pb-2">
        <h2 className="text-xl font-bold">Flowchart Editor</h2>
        <button
          className="text-gray-500 hover:text-gray-700 text-2xl"
          onClick={() => setIsFlowchartModalOpen(false)}
        >
          ×
        </button>
      </div>
      <div className="h-[60vh] border border-gray-300 rounded p-4 bg-gray-50">
        <FlowchartEditor
          onInsert={handleInsertFlowchart}
          onCancel={() => setIsFlowchartModalOpen(false)}
        />
      </div>
    </div>
  </div>
)}
</div>
  );
};

export default ContentEditor;